import { supabase } from '@/integrations/supabase/client';
import type {
  ProjectTimeEntry,
  ProjectTimeEntryInsert,
  ProjectTimeEntryUpdate,
} from '@/types/project.types';
import { projectService } from './projectService';

export const timeTrackingService = {
  // Fetch all time entries for a project
  async fetchProjectTimeEntries(projectId: string, filters?: {
    user_id?: string;
    date_from?: string;
    date_to?: string;
    billable?: boolean;
    approval_status?: string;
  }): Promise<ProjectTimeEntry[]> {
    let query = supabase
      .from('project_time_entries')
      .select(`
        *,
        user:user_id (id, first_name, last_name, email),
        approved_by_user:approved_by (id, first_name, last_name, email)
      `)
      .eq('project_id', projectId);

    if (filters?.user_id) {
      query = query.eq('user_id', filters.user_id);
    }
    
    if (filters?.date_from) {
      query = query.gte('entry_date', filters.date_from);
    }
    
    if (filters?.date_to) {
      query = query.lte('entry_date', filters.date_to);
    }
    
    if (filters?.billable !== undefined) {
      query = query.eq('billable', filters.billable);
    }
    
    if (filters?.approval_status) {
      query = query.eq('approval_status', filters.approval_status);
    }

    const { data, error } = await query.order('entry_date', { ascending: false });

    if (error) throw error;
    return data as ProjectTimeEntry[];
  },

  // Fetch time entries for a user
  async fetchUserTimeEntries(userId: string, filters?: {
    date_from?: string;
    date_to?: string;
    project_id?: string;
  }): Promise<ProjectTimeEntry[]> {
    let query = supabase
      .from('project_time_entries')
      .select(`
        *,
        project:project_id (id, name, project_code),
        approved_by_user:approved_by (id, first_name, last_name, email)
      `)
      .eq('user_id', userId);

    if (filters?.project_id) {
      query = query.eq('project_id', filters.project_id);
    }
    
    if (filters?.date_from) {
      query = query.gte('entry_date', filters.date_from);
    }
    
    if (filters?.date_to) {
      query = query.lte('entry_date', filters.date_to);
    }

    const { data, error } = await query.order('entry_date', { ascending: false });

    if (error) throw error;
    return data as ProjectTimeEntry[];
  },

  // Create a new time entry
  async createTimeEntry(entry: ProjectTimeEntryInsert): Promise<ProjectTimeEntry> {
    const { data: userData } = await supabase.auth.getUser();
    
    // Calculate hours if start and end times are provided
    let hoursLogged = entry.hours_logged;
    if (entry.start_time && entry.end_time) {
      const start = new Date(`2000-01-01T${entry.start_time}`);
      const end = new Date(`2000-01-01T${entry.end_time}`);
      const diffMs = end.getTime() - start.getTime();
      hoursLogged = diffMs / (1000 * 60 * 60); // Convert to hours
    }

    const { data, error } = await supabase
      .from('project_time_entries')
      .insert({
        ...entry,
        user_id: entry.user_id || userData?.user?.id,
        hours_logged: hoursLogged,
      })
      .select()
      .single();

    if (error) throw error;

    // Log activity
    await projectService.logActivity({
      project_id: entry.project_id,
      activity_type: 'time_entry_created',
      title: 'Time Entry Created',
      description: `${hoursLogged} hours logged for "${entry.task_description}"`,
      metadata: {
        hours: hoursLogged,
        billable: entry.billable,
        date: entry.entry_date,
      },
    });

    return data as ProjectTimeEntry;
  },

  // Update a time entry
  async updateTimeEntry(id: string, update: ProjectTimeEntryUpdate): Promise<ProjectTimeEntry> {
    // Get current entry for activity logging
    const { data: currentEntry } = await supabase
      .from('project_time_entries')
      .select('*')
      .eq('id', id)
      .single();

    // Recalculate hours if times are updated
    const updatedData = { ...update };
    if (update.start_time && update.end_time) {
      const start = new Date(`2000-01-01T${update.start_time}`);
      const end = new Date(`2000-01-01T${update.end_time}`);
      const diffMs = end.getTime() - start.getTime();
      updatedData.hours_logged = diffMs / (1000 * 60 * 60);
    }

    const { data, error } = await supabase
      .from('project_time_entries')
      .update(updatedData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Log activity for approval status changes
    if (currentEntry && data && update.approval_status && update.approval_status !== currentEntry.approval_status) {
      let activityTitle = 'Time Entry Updated';
      let activityDescription = 'Time entry updated';
      
      if (update.approval_status === 'approved') {
        activityTitle = 'Time Entry Approved';
        activityDescription = `${data.hours_logged} hours approved`;
      } else if (update.approval_status === 'rejected') {
        activityTitle = 'Time Entry Rejected';
        activityDescription = `${data.hours_logged} hours rejected`;
      }

      await projectService.logActivity({
        project_id: data.project_id,
        activity_type: 'time_entry_updated',
        title: activityTitle,
        description: activityDescription,
        metadata: {
          approved_by: update.approved_by,
          hours: data.hours_logged,
        },
      });
    }

    return data as ProjectTimeEntry;
  },

  // Delete a time entry
  async deleteTimeEntry(id: string): Promise<void> {
    // Get entry info for activity logging
    const { data: entry } = await supabase
      .from('project_time_entries')
      .select('project_id, hours_logged, task_description')
      .eq('id', id)
      .single();

    const { error } = await supabase
      .from('project_time_entries')
      .delete()
      .eq('id', id);

    if (error) throw error;

    if (entry) {
      await projectService.logActivity({
        project_id: entry.project_id,
        activity_type: 'time_entry_deleted',
        title: 'Time Entry Deleted',
        description: `${entry.hours_logged} hours for "${entry.task_description}" deleted`,
      });
    }
  },

  // Approve time entries
  async approveTimeEntries(entryIds: string[], approvedBy: string): Promise<void> {
    const { error } = await supabase
      .from('project_time_entries')
      .update({
        approval_status: 'approved',
        approved_by: approvedBy,
      })
      .in('id', entryIds);

    if (error) throw error;
  },

  // Reject time entries
  async rejectTimeEntries(entryIds: string[], rejectedBy: string, reason?: string): Promise<void> {
    const { error } = await supabase
      .from('project_time_entries')
      .update({
        approval_status: 'rejected',
        approved_by: rejectedBy,
        notes: reason,
      })
      .in('id', entryIds);

    if (error) throw error;
  },

  // Calculate time statistics
  async getTimeStats(projectId: string, options?: {
    date_from?: string;
    date_to?: string;
    group_by?: 'user' | 'date' | 'type';
  }): Promise<{
    total_hours: number;
    billable_hours: number;
    non_billable_hours: number;
    total_cost: number;
    average_hours_per_day: number;
    by_user?: Array<{
      user_id: string;
      user_name: string;
      total_hours: number;
      billable_hours: number;
      cost: number;
    }>;
    by_date?: Array<{
      date: string;
      hours: number;
      cost: number;
    }>;
    by_type?: Array<{
      type: string;
      hours: number;
      cost: number;
    }>;
  }> {
    let query = supabase
      .from('project_time_entries')
      .select('*')
      .eq('project_id', projectId);

    if (options?.date_from) {
      query = query.gte('entry_date', options.date_from);
    }
    
    if (options?.date_to) {
      query = query.lte('entry_date', options.date_to);
    }

    const { data: entries, error } = await query;
    
    if (error) throw error;

    const stats: any = {
      total_hours: 0,
      billable_hours: 0,
      non_billable_hours: 0,
      total_cost: 0,
      average_hours_per_day: 0,
    };

    if (!entries || entries.length === 0) {
      return stats;
    }

    // Calculate basic stats
    entries.forEach(entry => {
      const hours = entry.hours_logged || 0;
      const cost = hours * (entry.billing_rate || 0);
      
      stats.total_hours += hours;
      stats.total_cost += cost;
      
      if (entry.billable) {
        stats.billable_hours += hours;
      } else {
        stats.non_billable_hours += hours;
      }
    });

    // Calculate average hours per day
    const uniqueDates = new Set(entries.map(e => e.entry_date));
    stats.average_hours_per_day = stats.total_hours / uniqueDates.size;

    // Group by requested field
    if (options?.group_by === 'user') {
      const userMap = new Map<string, any>();
      
      for (const entry of entries) {
        const userId = entry.user_id;
        if (!userMap.has(userId)) {
          // Fetch user details
          const { data: user } = await supabase
            .from('profiles')
            .select('first_name, last_name')
            .eq('id', userId)
            .single();
          
          userMap.set(userId, {
            user_id: userId,
            user_name: user ? `${user.first_name} ${user.last_name}` : 'Unknown',
            total_hours: 0,
            billable_hours: 0,
            cost: 0,
          });
        }
        
        const userStats = userMap.get(userId);
        const hours = entry.hours_logged || 0;
        const cost = hours * (entry.billing_rate || 0);
        
        userStats.total_hours += hours;
        userStats.cost += cost;
        if (entry.billable) {
          userStats.billable_hours += hours;
        }
      }
      
      stats.by_user = Array.from(userMap.values());
    } else if (options?.group_by === 'date') {
      const dateMap = new Map<string, any>();
      
      entries.forEach(entry => {
        const date = entry.entry_date;
        if (!dateMap.has(date)) {
          dateMap.set(date, { date, hours: 0, cost: 0 });
        }
        
        const dateStats = dateMap.get(date);
        const hours = entry.hours_logged || 0;
        const cost = hours * (entry.billing_rate || 0);
        
        dateStats.hours += hours;
        dateStats.cost += cost;
      });
      
      stats.by_date = Array.from(dateMap.values()).sort((a, b) => a.date.localeCompare(b.date));
    } else if (options?.group_by === 'type') {
      const typeMap = new Map<string, any>();
      
      entries.forEach(entry => {
        const type = entry.time_entry_type;
        if (!typeMap.has(type)) {
          typeMap.set(type, { type, hours: 0, cost: 0 });
        }
        
        const typeStats = typeMap.get(type);
        const hours = entry.hours_logged || 0;
        const cost = hours * (entry.billing_rate || 0);
        
        typeStats.hours += hours;
        typeStats.cost += cost;
      });
      
      stats.by_type = Array.from(typeMap.values());
    }

    return stats;
  },

  // Sync with HIVE
  async syncWithHive(projectId: string, hiveProjectId: string): Promise<void> {
    // This would integrate with HIVE API
    // For now, just log the activity
    await projectService.logActivity({
      project_id: projectId,
      activity_type: 'hive_sync',
      title: 'HIVE Sync',
      description: `Synced time entries with HIVE project ${hiveProjectId}`,
      metadata: {
        hive_project_id: hiveProjectId,
        sync_date: new Date().toISOString(),
      },
    });

    // Update all entries with HIVE project ID
    await supabase
      .from('project_time_entries')
      .update({ hive_project_id: hiveProjectId })
      .eq('project_id', projectId)
      .is('hive_project_id', null);
  },

  // Generate timesheet report
  async generateTimesheet(userId: string, startDate: string, endDate: string): Promise<{
    user: any;
    period: { start: string; end: string };
    entries: ProjectTimeEntry[];
    summary: {
      total_hours: number;
      billable_hours: number;
      projects: Array<{
        project_id: string;
        project_name: string;
        hours: number;
        billable_hours: number;
      }>;
    };
  }> {
    // Fetch user details
    const { data: user } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();

    // Fetch time entries
    const entries = await this.fetchUserTimeEntries(userId, {
      date_from: startDate,
      date_to: endDate,
    });

    // Calculate summary
    const projectMap = new Map<string, any>();
    let totalHours = 0;
    let billableHours = 0;

    entries.forEach(entry => {
      const hours = entry.hours_logged || 0;
      totalHours += hours;
      
      if (entry.billable) {
        billableHours += hours;
      }

      const projectId = entry.project_id;
      if (!projectMap.has(projectId)) {
        projectMap.set(projectId, {
          project_id: projectId,
          project_name: (entry as any).project?.name || 'Unknown Project',
          hours: 0,
          billable_hours: 0,
        });
      }

      const projectStats = projectMap.get(projectId);
      projectStats.hours += hours;
      if (entry.billable) {
        projectStats.billable_hours += hours;
      }
    });

    return {
      user,
      period: { start: startDate, end: endDate },
      entries,
      summary: {
        total_hours: totalHours,
        billable_hours: billableHours,
        projects: Array.from(projectMap.values()),
      },
    };
  },
};